import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { AssetManagement } from '@aptsi-types';

import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { DataAssetSourceService } from '../services/data-asset-source.service';
import { WorkflowNavigationService } from '../../shared/workflow-navigation.service';

@Component({
  selector: 'app-data-asset-manual-create-or-update',
  templateUrl: './data-asset-manual-create-or-update.component.html',
  styleUrls: ['./data-asset-manual-create-or-update.component.scss']
})
export class DataAssetManualCreateOrUpdateComponent implements OnInit {
  form!: FormGroup;

  isCreate = true;
  dataAssetId: string | null = null;
  organizationId = '';

  loading = false;
  saving = false;
  completionMessage = '';
  pageError = '';

  importedBy = '';
  importedByKeycloakId = '';
  private existingImportedAt = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly dataAssetSourceService: DataAssetSourceService,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly dialog: MatDialog,
    private readonly workflowNavigation: WorkflowNavigationService
  ) { }

  ngOnInit(): void {
    this.dataAssetId =
      this.readParam('assetId') ||
      this.readParam('dataAssetId') ||
      this.readParam('dataassetid');

    const createFlag = this.route.snapshot.data['create'];
    this.isCreate = createFlag !== undefined ? Boolean(createFlag) : !this.dataAssetId;

    this.organizationId = this.getOrganizationIdFromContext();
    this.importedBy = this.getImportedByFromKeycloak();
    this.importedByKeycloakId = this.getKeycloakUserId();
    this.buildForm();

    if (!this.isCreate) {
      if (!this.dataAssetId) {
        this.openErrorDialog('Data Asset ID is missing.');
        return;
      }
      this.loadManualDataAsset(this.dataAssetId);
    }
  }

  get pageTitle(): string {
    return this.isCreate ? 'Add Data Asset' : 'Update Data Asset';
  }

  get pageDescription(): string {
    return this.isCreate
      ? 'Create a data asset manually by entering its identity and metadata.'
      : 'Review and update the details for this manually created data asset.';
  }

  get controls(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  private buildForm(): void {
    // Per the requested validation rules, only name and status are required.
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      description: [''],
      status: ['ACTIVE', [Validators.required, Validators.pattern(/.*\S.*/)]],
      assetTechnicalName: [''],
      qualifiedName: [''],
      creationTimestamp: [''],
      createdBy: [''],
      lastModifiedTimestamp: [''],
      lastModifiedBy: [''],
      entityVersion: [''],
      entityStatus: [''],
      collectionId: [''],
      classificationName: [''],
      upstreamAssetRelationships: [''],
      uniqueIdentifier: ['']
    });
  }

  private readParam(key: string): string | null {
    let current: ActivatedRoute | null = this.route;
    while (current) {
      const value = current.snapshot.paramMap.get(key);
      if (value) {
        return value;
      }
      current = current.parent;
    }
    return null;
  }

  private getTokenClaims(): Record<string, unknown> {
    try {
      const keycloak = this.keycloakService.getKeycloakInstance();
      return (keycloak?.tokenParsed || {}) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private getImportedByFromKeycloak(): string {
    const token = this.getTokenClaims();
    const userLabel = token['preferred_username'] || token['name'] || token['email'];
    return userLabel ? String(userLabel) : '';
  }

  private getKeycloakUserId(): string {
    try {
      const stateUserId = this.keycloakStateService.getUserId();
      if (stateUserId) {
        return String(stateUserId);
      }
    } catch {
      // Fall back to the subject claim below.
    }

    const subject = this.getTokenClaims()['sub'];
    return subject ? String(subject) : '';
  }

  private getOrganizationIdFromContext(): string {
    const queryValue =
      this.route.snapshot.queryParamMap.get('organizationId') ||
      this.route.snapshot.queryParamMap.get('organization_id');
    if (queryValue?.trim()) {
      return queryValue.trim();
    }

    const claims = this.getTokenClaims();
    const candidate =
      claims['organizationId'] ??
      claims['organization_id'] ??
      claims['organizationID'] ??
      claims['orgId'] ??
      claims['org_id'];

    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }

    const organizationClaim = claims['organization'];
    if (typeof organizationClaim === 'string' && organizationClaim.trim()) {
      return organizationClaim.trim();
    }
    if (organizationClaim && typeof organizationClaim === 'object') {
      const organization = organizationClaim as Record<string, unknown>;
      const id = organization['organizationId'] ?? organization['id'] ?? organization['orgId'];
      if (id !== null && id !== undefined && String(id).trim()) {
        return String(id).trim();
      }
    }

    // Some deployments persist the active organization separately from the token.
    try {
      for (const key of ['organizationId', 'organization_id', 'orgId', 'org_id']) {
        const storedValue = localStorage.getItem(key);
        if (storedValue?.trim()) {
          return storedValue.trim();
        }
      }
    } catch {
      // Storage may be unavailable; leave the ID empty and show a save-time message.
    }

    return '';
  }

  loadManualDataAsset(dataAssetId: string): void {
    this.loading = true;
    this.pageError = '';

    this.dataAssetSourceService.getManualDataAsset(dataAssetId).subscribe({
      next: (response: AssetManagement) => {
        this.loading = false;
        if (!response) {
          this.openErrorDialog('No details were found for this data asset.');
          return;
        }

        this.organizationId = String(response.organizationId ?? this.organizationId ?? '').trim();
        this.existingImportedAt = String(response.importedAt ?? '').trim();
        this.importedBy = String(response.importedBy ?? this.importedBy ?? '').trim();
        this.importedByKeycloakId = String(
          response.importedByKeycloakId ?? this.importedByKeycloakId ?? ''
        ).trim();

        this.form.patchValue({
          name: response.name ?? '',
          description: response.description ?? '',
          status: response.status ?? '',
          assetTechnicalName: response.assetTechnicalName ?? '',
          qualifiedName: response.qualifiedName ?? '',
          creationTimestamp: response.creationTimestamp ?? '',
          createdBy: response.createdBy ?? '',
          lastModifiedTimestamp: response.lastModifiedTimestamp ?? '',
          lastModifiedBy: response.lastModifiedBy ?? '',
          entityVersion: response.entityVersion ?? '',
          entityStatus: response.entityStatus ?? '',
          collectionId: response.collectionId ?? '',
          classificationName: this.normalizeClassification(response.classificationName),
          upstreamAssetRelationships: response.upstreamAssetRelationships ?? '',
          uniqueIdentifier: response.uniqueIdentifier ?? ''
        });
      },
      error: error => {
        this.loading = false;
        this.openErrorDialog(
          this.extractMessage(error) || 'Unable to load the data asset details.'
        );
      }
    });
  }

  save(): void {
    if (this.saving || this.loading) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.pageError = 'Enter a name and select or enter a status before saving.';
      return;
    }

    if (!this.organizationId) {
      this.pageError =
        'Organization ID is unavailable. Ensure it is provided in the Keycloak token or as the organizationId query parameter.';
      return;
    }

    if (!this.importedByKeycloakId) {
      this.pageError = 'Unable to identify the signed-in user. Please sign in again and retry.';
      return;
    }

    if (!this.isCreate && !this.dataAssetId) {
      this.pageError = 'Data Asset ID is missing. Unable to update this asset.';
      return;
    }

    this.pageError = '';
    this.saving = true;

    const value = this.form.getRawValue();
    const fields = {
      name: this.asText(value.name).trim(),
      description: this.asText(value.description).trim(),
      status: this.asText(value.status).trim(),
      assetTechnicalName: this.asText(value.assetTechnicalName).trim(),
      qualifiedName: this.asText(value.qualifiedName).trim(),
      creationTimestamp: this.asText(value.creationTimestamp).trim(),
      createdBy: this.asText(value.createdBy).trim(),
      lastModifiedTimestamp: this.asText(value.lastModifiedTimestamp).trim(),
      lastModifiedBy: this.asText(value.lastModifiedBy).trim(),
      entityVersion: this.asText(value.entityVersion).trim(),
      entityStatus: this.asText(value.entityStatus).trim(),
      collectionId: this.asText(value.collectionId).trim(),
      classificationName: this.asText(value.classificationName).trim(),
      upstreamAssetRelationships: this.asText(value.upstreamAssetRelationships).trim(),
      importedAt: this.existingImportedAt || new Date().toISOString(),
      uniqueIdentifier: this.asText(value.uniqueIdentifier).trim(),
      importedBy: this.importedBy,
      importedByKeycloakId: this.importedByKeycloakId
    };

    if (this.isCreate) {
      this.dataAssetSourceService.createManualDataAsset(
        this.organizationId,
        fields.name,
        fields.description,
        fields.status,
        fields.assetTechnicalName,
        fields.qualifiedName,
        fields.creationTimestamp,
        fields.createdBy,
        fields.lastModifiedTimestamp,
        fields.lastModifiedBy,
        fields.entityVersion,
        fields.entityStatus,
        fields.collectionId,
        fields.classificationName,
        fields.upstreamAssetRelationships,
        fields.importedAt,
        fields.uniqueIdentifier,
        fields.importedBy,
        fields.importedByKeycloakId
      ).subscribe({
        next: response => this.handleSaveResponse(response, true),
        error: error => this.onSaveError(error, 'Unable to create the data asset.')
      });
      return;
    }

    this.dataAssetSourceService.updateManualDataAsset(
      this.dataAssetId as string,
      this.organizationId,
      fields.name,
      fields.description,
      fields.status,
      fields.assetTechnicalName,
      fields.qualifiedName,
      fields.creationTimestamp,
      fields.createdBy,
      fields.lastModifiedTimestamp,
      fields.lastModifiedBy,
      fields.entityVersion,
      fields.entityStatus,
      fields.collectionId,
      fields.classificationName,
      fields.upstreamAssetRelationships,
      fields.importedAt,
      fields.uniqueIdentifier,
      fields.importedBy,
      fields.importedByKeycloakId
    ).subscribe({
      next: response => this.handleSaveResponse(response, false),
      error: error => this.onSaveError(error, 'Unable to update the data asset.')
    });
  }

  private handleSaveResponse(response: AssetManagement, isCreate: boolean): void {
    const message = this.extractMessage(response);
    if (message.toLocaleLowerCase().includes('already exists')) {
      this.saving = false;
      this.openErrorDialog(message);
      return;
    }
    this.onSaveSuccess(response, isCreate);
  }

  private onSaveSuccess(response: AssetManagement, isCreate: boolean): void {
    this.saving = false;
    this.completionMessage = this.extractMessage(response) ||
      (isCreate ? 'Data asset created successfully.' : 'Data asset updated successfully.');

    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: '380px',
      data: {
        isCreate,
        isCreateUpdate: true,
        title: 'Data Asset',
        message: this.completionMessage
      }
    });

    dialogRef.afterClosed().subscribe(() => this.navigateBack());
  }

  private onSaveError(error: unknown, fallbackMessage: string): void {
    this.saving = false;
    this.openErrorDialog(this.extractMessage(error) || fallbackMessage);
  }

  cancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
         void this.workflowNavigation.navigateToWorkflow(
    'dataAssetCapture'
  );
  }

  navigateToDataAssetCapture(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'dataAssetCapture'
  );
}

  private openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '380px',
      data: { errorMessage }
    });
  }

  private extractMessage(responseOrError: unknown): string {
    if (!responseOrError) {
      return '';
    }
    if (typeof responseOrError === 'string') {
      return responseOrError;
    }
    if (typeof responseOrError !== 'object') {
      return '';
    }

    const candidate = responseOrError as {
      message?: unknown;
      error?: { message?: unknown; errorMessage?: unknown; details?: unknown } | string;
      errorMessage?: unknown;
    };
    const nested = candidate.error;
    const candidates: unknown[] = [
      candidate.message,
      typeof nested === 'object' && nested !== null ? nested.message : nested,
      typeof nested === 'object' && nested !== null ? nested.errorMessage : undefined,
      typeof nested === 'object' && nested !== null ? nested.details : undefined,
      candidate.errorMessage
    ];
    const message = candidates.find(item => typeof item === 'string' && item.trim());
    return message ? String(message).trim() : '';
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return Boolean(control && control.invalid && (control.touched || control.dirty));
  }

  private normalizeClassification(value: string | string[] | null | undefined): string {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value ?? '';
  }

  private asText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }
}
